import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, formatDateTime, formatDate } from "@/lib/api";
import { PageHeader, StatusBadge, EmptyState } from "@/components/app/ui-bits";
import {
  Plus, FileText, Eye, Trash, PaperPlaneTilt, CheckCircle, XCircle,
  ArrowCounterClockwise, NotePencil,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
  DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useAuth, hasMinRole } from "@/contexts/AuthContext";
import AuditTrailDrawer from "@/components/app/AuditTrailDrawer";
import ExportMenu from "@/components/app/ExportMenu";

const TYPES = [
  { value: "daily",            label: "Daily Report" },
  { value: "weekly",           label: "Weekly Report" },
  { value: "monthly",          label: "Monthly Report" },
  { value: "project_progress", label: "Project Progress" },
  { value: "field",            label: "Field Report" },
  { value: "activity",         label: "Activity Report" },
  { value: "supervision",      label: "Supervision Report" },
  { value: "incident",         label: "Incident Report" },
  { value: "meeting",          label: "Meeting Report" },
  { value: "performance",      label: "Performance / Accomplishment" },
  { value: "custom",           label: "Custom" },
];

const BLANK = {
  title: "", report_type: "daily", custom_type_label: "",
  project_id: "", project_name: "", department: "",
  reporting_period_start: "", reporting_period_end: "",
  summary: "", content: "", reviewer_email: "",
};

function ReportForm({ initial, projects, onSave, trigger }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initial || BLANK);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm(initial || BLANK); }, [open, initial]);
  const setField = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      // Empty project_id should be omitted to avoid spurious link
      if (!payload.project_id) {
        delete payload.project_id; delete payload.project_name;
      } else {
        const p = projects.find((x) => x.id === payload.project_id);
        if (p) payload.project_name = p.name;
      }
      if (payload.report_type !== "custom") delete payload.custom_type_label;
      await onSave(payload);
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-display">
            {initial?.id ? "Edit report" : "New report"}
          </DialogTitle>
          <DialogDescription>
            Reports stay private to you (and your reviewer) until submitted.
            Submitted reports are visible to your department manager.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <Label>Title *</Label>
            <Input required value={form.title} onChange={(e) => setField("title", e.target.value)}
              data-testid="report-title" />
          </div>
          <div>
            <Label>Report type *</Label>
            <Select value={form.report_type} onValueChange={(v) => setField("report_type", v)}>
              <SelectTrigger data-testid="report-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {form.report_type === "custom" && (
            <div>
              <Label>Custom label</Label>
              <Input value={form.custom_type_label || ""} onChange={(e) => setField("custom_type_label", e.target.value)}
                placeholder="e.g. Post-mortem, Site Diary" />
            </div>
          )}
          <div className={form.report_type === "custom" ? "col-span-2" : ""}>
            <Label>Linked project (optional)</Label>
            <Select value={form.project_id || "_none"} onValueChange={(v) => setField("project_id", v === "_none" ? "" : v)}>
              <SelectTrigger data-testid="report-project"><SelectValue placeholder="No linked project" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">— No project —</SelectItem>
                {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Reporting period · start</Label>
            <Input type="date" value={form.reporting_period_start || ""} onChange={(e) => setField("reporting_period_start", e.target.value)} />
          </div>
          <div>
            <Label>Reporting period · end</Label>
            <Input type="date" value={form.reporting_period_end || ""} onChange={(e) => setField("reporting_period_end", e.target.value)} />
          </div>
          <div className="col-span-2">
            <Label>Reviewer email (optional)</Label>
            <Input type="email" value={form.reviewer_email || ""}
              onChange={(e) => setField("reviewer_email", e.target.value)}
              placeholder="manager@descon.group" />
          </div>
          <div className="col-span-2">
            <Label>Summary *</Label>
            <Textarea required rows={3} value={form.summary} onChange={(e) => setField("summary", e.target.value)}
              data-testid="report-summary" />
          </div>
          <div className="col-span-2">
            <Label>Detailed content</Label>
            <Textarea rows={8} value={form.content || ""} onChange={(e) => setField("content", e.target.value)}
              placeholder="Full report body — observations, actions, blockers, recommendations…"
              data-testid="report-content" />
          </div>
          <DialogFooter className="col-span-2">
            <Button type="submit" disabled={busy} className="btn-primary-blue" data-testid="report-save">
              {busy ? "Saving…" : "Save draft"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ReviewDialog({ report, onReviewed }) {
  const [open, setOpen] = useState(false);
  const [decision, setDecision] = useState("approved");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/reports/${report.id}/review`, { decision, comment });
      toast.success(`Report ${decision}`);
      setOpen(false); setComment(""); onReviewed?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`review-${report.id}`}>
          <CheckCircle size={12} className="mr-1" /> Review
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-display">Review report</DialogTitle>
          <DialogDescription>{report.title}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Decision</Label>
            <Select value={decision} onValueChange={setDecision}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="approved">✓ Approve</SelectItem>
                <SelectItem value="returned">↺ Return for revision</SelectItem>
                <SelectItem value="rejected">✗ Reject</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Comment</Label>
            <Textarea rows={4} value={comment} onChange={(e) => setComment(e.target.value)}
              placeholder="Optional feedback to the author…" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={busy} className="btn-primary-blue" data-testid="submit-review">
              {busy ? "Submitting…" : "Submit decision"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ReportDetail({ report, projects, refresh }) {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const isOwner = report.submitted_by_email === user?.email;
  const isManager = hasMinRole(user, "manager");
  const isAdmin = hasMinRole(user, "admin");
  const canEdit = (isOwner || isAdmin) && ["draft", "returned"].includes(report.status);
  const canSubmit = (isOwner || isAdmin) && ["draft", "returned"].includes(report.status);
  const canReview = isManager && ["submitted", "in_review"].includes(report.status);

  const doSubmit = async () => {
    try { await api.post(`/reports/${report.id}/submit`); toast.success("Submitted"); refresh(); setOpen(false); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const doDelete = async () => {
    try { await api.delete(`/reports/${report.id}`); toast.success("Deleted"); refresh(); setOpen(false); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`view-${report.id}`}>
          <Eye size={12} />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-display">{report.title}</DialogTitle>
          <DialogDescription className="flex items-center gap-2 flex-wrap">
            <span className="overline">{TYPES.find((t) => t.value === report.report_type)?.label || report.report_type}</span>
            <StatusBadge status={report.status} />
            {report.project_name && <span className="text-xs">· {report.project_name}</span>}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div><span className="overline block mb-1">Submitted by</span>{report.submitted_by_name || report.submitted_by_email}</div>
            <div><span className="overline block mb-1">Submitted</span>{report.submission_date ? formatDateTime(report.submission_date) : "—"}</div>
            {report.reporting_period_start && (
              <div className="col-span-2">
                <span className="overline block mb-1">Period</span>
                {formatDate(report.reporting_period_start)} → {formatDate(report.reporting_period_end || report.reporting_period_start)}
              </div>
            )}
          </div>
          <div>
            <div className="overline mb-1">Summary</div>
            <div className="rounded-lg bg-slate-50 p-3 whitespace-pre-wrap">{report.summary}</div>
          </div>
          {report.content && (
            <div>
              <div className="overline mb-1">Content</div>
              <div className="rounded-lg bg-slate-50 p-3 whitespace-pre-wrap text-slate-700 max-h-80 overflow-auto">{report.content}</div>
            </div>
          )}
          {report.review && (
            <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3">
              <div className="overline text-indigo-800 mb-1">Reviewer · {report.review.decision}</div>
              <div className="text-xs text-slate-700 mb-1">{report.review.reviewed_by_email} · {formatDateTime(report.review.reviewed_at)}</div>
              {report.review.comment && <div className="text-sm">{report.review.comment}</div>}
            </div>
          )}
          {report.history?.length > 0 && (
            <details className="text-xs">
              <summary className="cursor-pointer overline">History ({report.history.length})</summary>
              <ul className="mt-2 space-y-1">
                {report.history.map((h, i) => (
                  <li key={i}>{formatDateTime(h.at)} · {h.by} · <strong>{h.action}</strong>{h.comment ? ` — ${h.comment}` : ""}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
        <DialogFooter className="gap-2 flex-wrap">
          {isManager && (
            <AuditTrailDrawer entity="report" entityId={report.id} label={`Report ${report.title}`} />
          )}
          {canEdit && (
            <ReportForm initial={report} projects={projects} onSave={async (f) => {
              await api.patch(`/reports/${report.id}`, f); toast.success("Updated"); refresh();
            }} trigger={<Button variant="outline"><NotePencil size={12} className="mr-1" /> Edit</Button>} />
          )}
          {canSubmit && (
            <Button onClick={doSubmit} className="btn-primary-blue" data-testid="submit-report">
              <PaperPlaneTilt size={12} className="mr-1" /> Submit for review
            </Button>
          )}
          {canReview && <ReviewDialog report={report} onReviewed={() => { refresh(); setOpen(false); }} />}
          {(isOwner || isAdmin) && ["draft", "returned"].includes(report.status) && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" variant="outline" className="text-red-700" data-testid={`delete-${report.id}`}>
                  <Trash size={12} />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this report?</AlertDialogTitle>
                  <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={doDelete}>Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Reports() {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all"); // all | mine | review_queue
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (tab === "mine") params.set("mine", "true");
      if (typeFilter) params.set("report_type", typeFilter);
      if (statusFilter) params.set("status", statusFilter);
      const [{ data: rep }, { data: projs }] = await Promise.all([
        api.get(`/reports?${params.toString()}`),
        api.get("/projects").catch(() => ({ data: [] })),
      ]);
      let items = rep?.items || [];
      if (tab === "review_queue") {
        items = items.filter((r) => ["submitted", "in_review"].includes(r.status));
      }
      setReports(items);
      setProjects(projs || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, typeFilter, statusFilter]);

  const counts = useMemo(() => {
    const byStatus = {};
    for (const r of reports) byStatus[r.status] = (byStatus[r.status] || 0) + 1;
    return byStatus;
  }, [reports]);

  const create = async (form) => {
    await api.post("/reports", form);
    toast.success("Report saved as draft");
    load();
  };

  const isManager = hasMinRole(user, "manager");

  return (
    <div data-testid="reports-page">
      <PageHeader
        eyebrow="My Reports"
        title="Reports"
        description="Create, submit and track your work reports. Managers can review reports from staff in their department."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <ExportMenu module="reports" />
            <ReportForm
              projects={projects}
              onSave={create}
              trigger={
                <Button className="btn-primary-blue" data-testid="new-report-btn">
                  <Plus size={14} className="mr-2" /> New report
                </Button>
              }
            />
          </div>
        }
      />
      <div className="mx-auto max-w-[1400px] p-4 md:p-8 space-y-5">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2">
          <Button variant={tab === "all" ? "default" : "outline"} size="sm" onClick={() => setTab("all")} data-testid="tab-all">All visible</Button>
          <Button variant={tab === "mine" ? "default" : "outline"} size="sm" onClick={() => setTab("mine")} data-testid="tab-mine">My reports</Button>
          {isManager && (
            <Button variant={tab === "review_queue" ? "default" : "outline"} size="sm" onClick={() => setTab("review_queue")} data-testid="tab-queue">
              Review queue {counts.submitted ? <span className="ml-1 rounded-full bg-amber-100 px-1.5 text-xs">{counts.submitted}</span> : null}
            </Button>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2">
          <Select value={typeFilter || "_all"} onValueChange={(v) => setTypeFilter(v === "_all" ? "" : v)}>
            <SelectTrigger className="h-9 w-56" data-testid="filter-type"><SelectValue placeholder="All types" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All types</SelectItem>
              {TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter || "_all"} onValueChange={(v) => setStatusFilter(v === "_all" ? "" : v)}>
            <SelectTrigger className="h-9 w-44" data-testid="filter-status"><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All statuses</SelectItem>
              {["draft", "submitted", "in_review", "approved", "rejected", "returned"].map((s) =>
                <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="font-mono-alt text-xs uppercase tracking-[0.25em] text-zinc-500">Loading…</div>
        ) : reports.length === 0 ? (
          <EmptyState icon={FileText} title="No reports yet"
            description="Click New report to file a daily, weekly, field, incident or any other report. Save as draft, then submit when ready." />
        ) : (
          <div className="border border-zinc-200 bg-white rounded-md">
            <table className="data-table w-full">
              <thead>
                <tr>
                  <th>Title</th><th>Type</th><th>Project</th><th>Period</th>
                  <th>Submitted by</th><th>Submitted</th><th>Status</th><th className="w-40">Actions</th>
                </tr>
              </thead>
              <tbody>
                {reports.map((r) => (
                  <tr key={r.id} data-testid={`report-row-${r.id}`}>
                    <td className="font-semibold max-w-xs truncate" title={r.title}>{r.title}</td>
                    <td className="uppercase tracking-wider text-xs">
                      {r.report_type === "custom" && r.custom_type_label ? r.custom_type_label : (TYPES.find((t) => t.value === r.report_type)?.label || r.report_type)}
                    </td>
                    <td className="text-xs">{r.project_name || <span className="text-slate-400">—</span>}</td>
                    <td className="text-xs">
                      {r.reporting_period_start ? `${formatDate(r.reporting_period_start)}${r.reporting_period_end && r.reporting_period_end !== r.reporting_period_start ? " → " + formatDate(r.reporting_period_end) : ""}` : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="text-xs">{r.submitted_by_name || r.submitted_by_email}</td>
                    <td className="font-mono-alt text-xs">{r.submission_date ? formatDateTime(r.submission_date) : <span className="text-slate-400">—</span>}</td>
                    <td><StatusBadge status={r.status} /></td>
                    <td><ReportDetail report={r} projects={projects} refresh={load} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
